import { isValidPhoneNumber, type CountryCode } from "libphonenumber-js/max";
import { z } from "zod";

export const schema = z.object({
  name: z
    .string()
    .min(1, "El nombre del usuario es obligatorio")
    .max(255, "La longitud máxima del nombre es de 255 caracteres")
    .nonempty()
    .refine((val) => {
      return val.trim().length > 0;
    }, "El nombre del usuario no puede estar vacío"),
  last_name: z
    .string()
    .min(1, "El apellido del usuario es obligatorio")
    .max(255, "La longitud máxima del apellido es de 255 caracteres")
    .nonempty()
    .refine((val) => {
      return val.trim().length > 0;
    }, "El apellido del usuario no puede estar vacío"),
  email: z
    .email("El correo electrónico debe ser válido")
    .max(255, "La longitud máxima del correo electrónico es de 255 caracteres")
    .nonempty()
    .refine((val) => {
      return val.trim().length > 0;
    }, "El correo electrónico no puede estar vacío"),
  dui: z
    .string()
    .regex(/^\d{8}-\d$/, "El número de DUI debe tener el formato 00000000-0")
    .refine((val) => {
      return val.trim().length > 0;
    }, "El número de DUI no puede estar vacío")
    .refine((val) => {
      return duiValidator(val.replace("-", ""));
    }, "El número de DUI no es válido"),
  phone_number: z
    .object({
      number: z.string().trim().min(1, "El número de teléfono es obligatorio"),
      calling_code: z
        .string()
        .trim()
        .min(1, "El código de país es obligatorio"),
      country: z.string().trim().min(1, "El país es obligatorio"),
    })
    .superRefine((value, ctx) => {
      if (!value.number) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["number"],
          message: "El número de teléfono es obligatorio",
        });

        return;
      }

      const isValid = isValidPhoneNumber(
        value.number,
        value.country as CountryCode,
      );

      if (!isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["number"],
          message: "Formato invalido",
        });
      }
    }),
  registered_position_id: z
    .string()
    .min(1, "La posición del usuario es obligatoria"),
  assigned_institution_id: z
    .string()
    .min(1, "La organización del usuario es obligatoria"),
  department_id: z
    .string()
    .min(1, "La unidad organizativa del usuario es obligatoria"),
  role_id: z.string().optional(),
});

export type NewUserFormData = z.infer<typeof schema>;

const duiValidator = (dui: string) => {
  if (!new RegExp(/\d{9}/i).test(dui)) return false;
  const duiArr = dui.split("").map(Number);
  const validator = duiArr.pop();
  let sum = 0;
  duiArr.forEach((digit, index) => {
    sum += digit * (9 - index);
  });
  const mod = sum % 10;
  if (mod === 0) return validator === 0;
  return validator === 10 - mod;
};
